import { useState } from "react";
import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import { CtaWhatsApp } from "../components/Cta";
import { Reveal, Tilt } from "../components/motion";
import { CardPair } from "../components/CardMockup";
import { GALLERY_DESIGNS, REALISATIONS } from "../data/gallery";
import { gradientFrom, readableOn } from "../lib/colors";

const FILTERS = ["Tous", "Restauration", "Beauté & bien-être", "Commerce", "Services"];

const SECTOR_TO_FILTER = {
  Restaurant: "Restauration",
  "Café de quartier": "Restauration",
  "Institut de beauté": "Beauté & bien-être",
  Boutique: "Commerce",
  Hôtel: "Services",
  "Garage automobile": "Services",
  "Agent immobilier": "Services",
  "Coach sportif": "Services",
  "Identité premium": "Services",
};

export default function Galerie() {
  const [filter, setFilter] = useState("Tous");
  const designs = GALLERY_DESIGNS.filter(
    (d) => filter === "Tous" || SECTOR_TO_FILTER[d.sector] === filter
  );

  return (
    <>
      <Seo
        title="Galerie de designs"
        description="Réalisations Noveocard et exemples de cartes digitales : noir & or, blanc & bleu, bordeaux, vert forêt, rose poudré… Finition premium recto-verso, sans limite de couleur."
        path="/galerie"
      />
      <PageHero
        eyebrow="Galerie & réalisations"
        titleLines={["Aucune limite de couleur.", "Que des finitions premium."]}
        description="Noir & or, blanc & bleu, bordeaux, vert forêt, rose poudré : votre carte prend vos couleurs, jamais l'inverse. Chaque carte est dessinée sur-mesure, recto comme verso."
      />

      {/* La finition livrée — cartes de référence */}
      <section className="grain relative overflow-hidden bg-nuit-950 py-16 text-white sm:py-24">
        <div className="container-nv">
          <Reveal className="max-w-2xl">
            <p className="eyebrow text-electric-300">La finition livrée</p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Voici le niveau de carte que vous recevez.
            </h2>
            <p className="mt-4 text-white/65">
              Effets lumineux bleu électrique, symbole NFC, verso complet avec vos accès directs
              (WhatsApp, site internet, avis Google, Instagram, TikTok). Votre logo et vos couleurs
              à la place des nôtres.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="mt-12 flex flex-col items-center justify-center gap-8 sm:flex-row sm:gap-10" data-testid="gallery-reference">
              <Tilt max={8} className="w-full max-w-md -rotate-2">
                <img
                  src="/assets/ref-recto.webp"
                  alt="Carte Noveocard recto : bleu nuit lumineux, logo N et slogan « Un simple geste ouvre un monde »"
                  loading="lazy"
                  width="1600"
                  height="1004"
                  className="w-full rounded-2xl shadow-[0_40px_80px_-30px_rgba(0,0,0,0.8)]"
                />
              </Tilt>
              <Tilt max={8} className="w-full max-w-md rotate-2 sm:translate-y-6">
                <img
                  src="/assets/ref-verso.webp"
                  alt="Carte Noveocard verso : « Approchez votre téléphone », accès WhatsApp, site internet, avis Google, Instagram et TikTok"
                  loading="lazy"
                  width="1600"
                  height="1004"
                  className="w-full rounded-2xl shadow-[0_40px_80px_-30px_rgba(0,0,0,0.8)]"
                />
              </Tilt>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Réalisations clients */}
      <section className="bg-white py-14 sm:py-20">
        <div className="container-nv">
          <Reveal className="max-w-2xl">
            <p className="eyebrow">Nos réalisations</p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-nuit-900 sm:text-4xl">
              Des cartes déjà entre les mains de nos clients.
            </h2>
            <p className="mt-4 text-steel">
              Couleurs, fonds illustrés ou texturés, dorure, logo sur mesure, verso dédié à l'action :
              chaque réalisation est unique.
            </p>
          </Reveal>
          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            {REALISATIONS.map((r, i) => (
              <Reveal key={r.id} delay={(i % 2) * 0.08}>
                <figure className="group" data-testid={`realisation-${r.id}`}>
                  <div className="overflow-hidden rounded-3xl border border-nuit-900/10 bg-nuit-950 shadow-[0_20px_50px_-24px_rgba(7,26,45,0.4)]">
                    <img
                      src={r.img}
                      alt={r.alt}
                      loading="lazy"
                      width="1400"
                      height="880"
                      className="aspect-[86/54] w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                    />
                  </div>
                  <figcaption className="mt-4 flex items-start justify-between gap-3">
                    <div>
                      <p className="font-display font-bold text-nuit-900">{r.client}</p>
                      <p className="text-sm text-steel">{r.detail}</p>
                    </div>
                    <span className="shrink-0 rounded-full bg-electric/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-electric">
                      Réalisation client
                    </span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Exemples de démonstration — mise en scène recto/verso */}
      <section className="grain relative overflow-hidden bg-nuit-950 py-14 text-white sm:py-20">
        <div className="container-nv">
          <Reveal className="max-w-2xl">
            <p className="eyebrow text-electric-300">Explorer les styles</p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Des exemples pour imaginer la vôtre.
            </h2>
          </Reveal>
          <div
            className="mt-8 rounded-2xl border border-white/15 bg-white/5 px-5 py-4 text-sm text-white/75"
            data-testid="gallery-demo-notice"
          >
            <strong className="font-display text-white">Exemples de démonstration.</strong> Les
            établissements ci-dessous sont fictifs : ils illustrent des styles possibles. Votre carte
            sera conçue à partir de votre identité visuelle réelle — logo, couleurs, fonds, univers.
          </div>

          <div className="mb-12 mt-6 flex flex-wrap gap-2" role="tablist" aria-label="Filtrer par secteur">
            {FILTERS.map((f) => (
              <button
                key={f}
                role="tab"
                aria-selected={filter === f}
                data-testid={`gallery-filter-${f.toLowerCase().replace(/[^a-z]/g, "-")}`}
                onClick={() => setFilter(f)}
                className={`rounded-full px-5 py-2.5 font-display text-sm font-semibold transition-colors duration-300 ${
                  filter === f
                    ? "bg-white text-nuit-900"
                    : "border border-white/20 text-white/75 hover:border-white/60 hover:text-white"
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          <div className="grid gap-12 lg:grid-cols-2">
            {designs.map((d, i) => (
              <Reveal key={d.id} delay={(i % 2) * 0.08}>
                <figure className="group" data-testid={`gallery-item-${d.id}`}>
                  <div className="rounded-3xl bg-white/[0.04] px-4 py-8 transition-colors duration-500 group-hover:bg-white/[0.08]">
                    <CardPair
                      name={d.name}
                      bg={gradientFrom(d.base)}
                      fg={readableOn(d.base)}
                      accent={d.accent}
                      pattern={d.pattern || null}
                      patternColor={d.accent}
                    />
                  </div>
                  <figcaption className="mt-5 flex items-start justify-between gap-3 px-1">
                    <div>
                      <p className="font-display font-bold text-white">{d.style}</p>
                      <p className="text-sm text-white/55">{d.sector}</p>
                    </div>
                    <span className="rounded-full bg-white/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white/60">
                      Démo
                    </span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>

          <div className="mt-16 flex flex-col items-center gap-3 text-center">
            <p className="max-w-xl font-display text-2xl font-bold sm:text-3xl">
              La vôtre n'existe pas encore.
            </p>
            <p className="max-w-lg text-white/65">
              Envoyez-nous votre logo et vos couleurs : nous dessinons un visuel sur-mesure, validé
              avec vous avant production.
            </p>
            <CtaWhatsApp
              message="Bonjour Noveocard, j'ai vu votre galerie de designs et je souhaite une carte à mon image."
              showResponseTime
              testid="gallery-cta-whatsapp"
            />
          </div>
        </div>
      </section>
    </>
  );
}
